export type PopoverPlacement = 'top' | 'bottom'

interface ChoosePlacementArgs {
  anchorTop: number
  anchorBottom: number
  popoverHeight: number
  viewportHeight: number
  preferred: PopoverPlacement
  gap: number
}

interface ChoosePlacementResult {
  placement: PopoverPlacement
  /** Set only when neither side has room and the popover must be clamped. */
  maxHeight?: number
}

/**
 * Pure decision: which side to open on, and whether the popover needs to be
 * clamped because neither side has room for it. Ties resolve to `preferred`
 * — jsdom returns an all-zero rect, so without this rule every popover would
 * flip in tests and diverge from real-DOM behavior.
 */
export const choosePopoverPlacement = ({
  anchorTop,
  anchorBottom,
  popoverHeight,
  viewportHeight,
  preferred,
  gap,
}: ChoosePlacementArgs): ChoosePlacementResult => {
  const roomAbove = anchorTop - gap
  const roomBelow = viewportHeight - anchorBottom - gap
  const roomForPreferred = preferred === 'top' ? roomAbove : roomBelow
  const roomForOther = preferred === 'top' ? roomBelow : roomAbove

  if (roomForPreferred >= popoverHeight) {
    return { placement: preferred }
  }
  if (roomForOther >= popoverHeight) {
    return { placement: preferred === 'top' ? 'bottom' : 'top' }
  }
  // Neither side fits — use whichever has more room and clamp to it.
  const placement =
    roomForOther > roomForPreferred
      ? preferred === 'top'
        ? 'bottom'
        : 'top'
      : preferred
  const maxHeight = Math.max(roomForOther, roomForPreferred)
  return { placement, maxHeight }
}

/** Directional shadow so a downward popover doesn't cast an upward shadow. */
export const POPOVER_SHADOW: Record<PopoverPlacement, string> = {
  top: '0 -20px 25px -5px rgba(0, 0, 0, 0.1), 0 -10px 10px -5px rgba(0, 0, 0, 0.04)',
  bottom:
    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
}
